/**
 * 外呼座席管理页面
 */

import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  message,
  Typography,
  Statistic,
  Row,
  Col,
  Tooltip,
} from 'antd';
import {
  PlusOutlined,
  PhoneOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  StopOutlined,
  DeleteOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { createSeat, getMySeat, syncSeatStatus, updateSeatStatus } from '../../services/outbound';
import { getUsers, User } from '../../services/user';
import { OutboundSeat, SeatStatus, CreateSeatRequest } from '../../types/outbound';
import { formatDateTime } from '../../utils/dateFormat';

const { Title, Text } = Typography;

const OutboundSeats: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [seats, setSeats] = useState<OutboundSeat[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [mySeat, setMySeat] = useState<OutboundSeat | null>(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedSeat, setSelectedSeat] = useState<OutboundSeat | null>(null);
  const [form] = Form.useForm();

  // 加载数据
  useEffect(() => {
    loadData();
    loadUsers();
    loadMySeat();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // 加载当前用户的座席
      const seat = await getMySeat();
      if (seat) {
        setSeats([seat]);
        setMySeat(seat);
      } else {
        setSeats([]);
        setMySeat(null);
      }
    } catch (error: any) {
      console.error('加载座席列表失败:', error);
      setSeats([]);
      setMySeat(null);
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await getUsers({ skip: 0, limit: 1000 });
      console.log('加载用户列表:', response);
      
      // API返回的可能是数组或包含items的对象
      const userList = Array.isArray(response) ? response : (response.items || []);
      setUsers(userList);
    } catch (error: any) {
      console.error('加载用户列表失败:', error);
      message.error('加载用户列表失败');
    }
  };

  const loadMySeat = async () => {
    try {
      const seat = await getMySeat();
      if (seat) {
        setMySeat(seat);
        setSeats([seat]);
      } else {
        setMySeat(null);
        setSeats([]);
      }
    } catch (error: any) {
      console.error('加载座席信息失败:', error);
      setMySeat(null);
      setSeats([]);
    }
  };

  // 创建座席
  const handleCreate = async (values: CreateSeatRequest) => {
    try {
      await createSeat(values);
      message.success('座席创建成功！验证短信已发送到绑定手机号，请回复验证码激活座席。');
      setCreateModalVisible(false);
      form.resetFields();
      loadData();
      loadMySeat();
    } catch (error: any) {
      message.error(error.message || '创建座席失败');
    }
  };

  // 同步座席状态
  const handleSyncStatus = async (seatId: number) => {
    try {
      setLoading(true);
      const updatedSeat = await syncSeatStatus(seatId);
      
      if (updatedSeat.is_verified) {
        message.success('座席已激活！现在可以使用外呼功能了。');
      } else {
        message.info('座席仍未验证，请确认已回复验证短信。');
      }
      
      loadData();
      loadMySeat();
    } catch (error: any) {
      message.error(error.message || '同步状态失败');
    } finally {
      setLoading(false);
    }
  };

  // 查看座席详情
  const handleViewDetail = (seat: OutboundSeat) => {
    setSelectedSeat(seat);
    setDetailModalVisible(true);
  };

  // 暂停/恢复座席
  const handleToggleStatus = async (seat: OutboundSeat) => {
    const newStatus = seat.status === SeatStatus.ACTIVE ? 'suspended' : 'active';
    const actionText = newStatus === 'suspended' ? '暂停' : '恢复';
    
    Modal.confirm({
      title: `确认${actionText}座席`,
      content: `确定要${actionText}座席 ${seat.seat_account} 吗？`,
      onOk: async () => {
        try {
          await updateSeatStatus(seat.id, newStatus);
          message.success(`座席已${actionText}`);
          loadData();
          loadMySeat();
        } catch (error: any) {
          message.error(error.message || `${actionText}座席失败`);
        }
      },
    });
  };

  // 座席状态标签
  const renderStatusTag = (status: SeatStatus, isVerified: boolean) => {
    if (status === SeatStatus.DELETED) {
      return <Tag color="default">已删除</Tag>;
    }
    if (status === SeatStatus.SUSPENDED) {
      return <Tag color="warning">已暂停</Tag>;
    }
    if (!isVerified) {
      return (
        <Tooltip title="请回复验证短信激活座席">
          <Tag icon={<ClockCircleOutlined />} color="processing">
            待验证
          </Tag>
        </Tooltip>
      );
    }
    if (status === SeatStatus.ACTIVE) {
      return (
        <Tag icon={<CheckCircleOutlined />} color="success">
          已激活
        </Tag>
      );
    }
    return <Tag>{status}</Tag>;
  };

  // 表格列定义
  const columns: ColumnsType<OutboundSeat> = [
    {
      title: '座席账号',
      dataIndex: 'seat_account',
      key: 'seat_account',
      width: 180,
    },
    {
      title: '绑定手机号',
      dataIndex: 'bind_number',
      key: 'bind_number',
      width: 130,
    },
    {
      title: '状态',
      key: 'status',
      width: 120,
      render: (_, record) => renderStatusTag(record.status, record.is_verified),
    },
    {
      title: '总呼叫次数',
      dataIndex: 'total_calls',
      key: 'total_calls',
      width: 120,
      align: 'right',
    },
    {
      title: '总通话时长',
      dataIndex: 'total_duration',
      key: 'total_duration',
      width: 120,
      align: 'right',
      render: (duration: number) => {
        const hours = Math.floor(duration / 3600);
        const minutes = Math.floor((duration % 3600) / 60);
        return `${hours}小时${minutes}分`;
      },
    },
    {
      title: '备注',
      dataIndex: 'remark',
      key: 'remark',
      ellipsis: true,
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (text: string) => formatDateTime(text),
    },
    {
      title: '操作',
      key: 'action',
      width: 200,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          {record.status === SeatStatus.PENDING && !record.is_verified && (
            <Tooltip title="从平台同步最新验证状态">
              <Button
                type="link"
                size="small"
                icon={<ReloadOutlined />}
                onClick={() => handleSyncStatus(record.id)}
              >
                同步状态
              </Button>
            </Tooltip>
          )}
          <Button type="link" size="small" onClick={() => handleViewDetail(record)}>
            详情
          </Button>
          {record.status === SeatStatus.ACTIVE && (
            <Button
              type="link"
              size="small"
              danger
              onClick={() => handleToggleStatus(record)}
            >
              暂停
            </Button>
          )}
          {record.status === SeatStatus.SUSPENDED && (
            <Button type="link" size="small" onClick={() => handleToggleStatus(record)}>
              恢复
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Space direction="vertical" size={24} style={{ width: '100%' }}>
        {/* 页面标题 */}
        <div>
          <Title level={3}>
            <PhoneOutlined /> 外呼座席管理
          </Title>
          <Text type="secondary">
            管理外呼座席，每个用户需要创建座席才能使用外呼功能
          </Text>
        </div>

        {/* 我的座席卡片 */}
        {mySeat && (
          <Card title="我的座席" size="small">
            <Row gutter={16}>
              <Col span={6}>
                <Statistic title="座席账号" value={mySeat.seat_account} />
              </Col>
              <Col span={6}>
                <Statistic title="绑定手机号" value={mySeat.bind_number} />
              </Col>
              <Col span={6}>
                <Statistic title="总呼叫次数" value={mySeat.total_calls} />
              </Col>
              <Col span={6}>
                <Statistic
                  title="总通话时长"
                  value={Math.floor(mySeat.total_duration / 60)}
                  suffix="分钟"
                />
              </Col>
            </Row>
            <div style={{ marginTop: 16 }}>
              状态：{renderStatusTag(mySeat.status, mySeat.is_verified)}
              {!mySeat.is_verified && (
                <Text type="warning" style={{ marginLeft: 16 }}>
                  请回复验证短信中的验证码激活座席
                </Text>
              )}
            </div>
          </Card>
        )}

        {/* 操作栏 */}
        <Card>
          <Space>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setCreateModalVisible(true);
                // 打开对话框时重新加载用户列表
                if (users.length === 0) {
                  loadUsers();
                }
              }}
            >
              创建座席
            </Button>
            <Button icon={<ReloadOutlined />} onClick={loadData}>
              刷新
            </Button>
          </Space>
        </Card>

        {/* 座席列表 */}
        <Card>
          <Table
            columns={columns}
            dataSource={seats}
            rowKey="id"
            loading={loading}
            scroll={{ x: 1200 }}
            pagination={{
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total) => `共 ${total} 条`,
            }}
          />
        </Card>
      </Space>

      {/* 创建座席对话框 */}
      <Modal
        title={`创建座席 (用户数: ${users.length})`}
        open={createModalVisible}
        onCancel={() => {
          setCreateModalVisible(false);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleCreate}
          initialValues={{ max_duration: 60 }}
        >
          <Form.Item
            label="选择用户"
            name="user_id"
            rules={[{ required: true, message: '请选择用户' }]}
          >
            <Select
              showSearch
              placeholder="请选择用户"
              filterOption={(input, option) =>
                option?.label?.toString().toLowerCase().includes(input.toLowerCase()) || false
              }
            >
              {users.map((user) => (
                <Select.Option key={user.id} value={user.id}>
                  {user.real_name || user.username}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="绑定手机号"
            name="bind_number"
            rules={[
              { required: true, message: '请输入手机号' },
              {
                pattern: /^1[3-9]\d{9}$/,
                message: '请输入正确的手机号',
              },
            ]}
            extra="此手机号将作为外呼时的主叫号码，创建后会收到验证短信"
          >
            <Input placeholder="请输入11位手机号" maxLength={11} />
          </Form.Item>

          <Form.Item label="备注" name="remark">
            <Input.TextArea
              placeholder="选填，如：销售座席、客服座席等"
              rows={3}
            />
          </Form.Item>

          <div
            style={{
              padding: 12,
              background: '#f0f5ff',
              border: '1px solid #adc6ff',
              borderRadius: 4,
            }}
          >
            <Text type="secondary">
              <strong>注意事项：</strong>
              <br />
              1. 座席创建后，绑定手机号会收到验证短信
              <br />
              2. 请直接回复短信中的验证码激活座席
              <br />
              3. 只有激活的座席才能发起外呼
              <br />
              4. 每个用户只能创建一个座席
            </Text>
          </div>
        </Form>
      </Modal>

      {/* 座席详情对话框 */}
      <Modal
        title="座席详情"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            关闭
          </Button>,
        ]}
        width={700}
      >
        {selectedSeat && (
          <div>
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Statistic title="座席账号" value={selectedSeat.seat_account} />
              </Col>
              <Col span={12}>
                <Statistic title="绑定手机号" value={selectedSeat.bind_number} />
              </Col>
              <Col span={12}>
                <div>
                  <div style={{ color: 'rgba(0, 0, 0, 0.45)', fontSize: 14, marginBottom: 8 }}>
                    状态
                  </div>
                  {renderStatusTag(selectedSeat.status, selectedSeat.is_verified)}
                </div>
              </Col>
              <Col span={12}>
                <div>
                  <div style={{ color: 'rgba(0, 0, 0, 0.45)', fontSize: 14, marginBottom: 8 }}>
                    验证状态
                  </div>
                  <Tag color={selectedSeat.is_verified ? 'success' : 'error'}>
                    {selectedSeat.is_verified ? '已验证' : '未验证'}
                  </Tag>
                </div>
              </Col>
              <Col span={12}>
                <Statistic title="总呼叫次数" value={selectedSeat.total_calls} suffix="次" />
              </Col>
              <Col span={12}>
                <Statistic
                  title="总通话时长"
                  value={Math.floor(selectedSeat.total_duration / 60)}
                  suffix="分钟"
                />
              </Col>
              <Col span={24}>
                <div style={{ marginTop: 16 }}>
                  <Text strong>创建时间：</Text>
                  <Text>{formatDateTime(selectedSeat.created_at)}</Text>
                </div>
                <div style={{ marginTop: 8 }}>
                  <Text strong>更新时间：</Text>
                  <Text>{formatDateTime(selectedSeat.updated_at)}</Text>
                </div>
                {selectedSeat.remark && (
                  <div style={{ marginTop: 8 }}>
                    <Text strong>备注：</Text>
                    <Text>{selectedSeat.remark}</Text>
                  </div>
                )}
              </Col>
            </Row>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default OutboundSeats;
